import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  makeVar,
  from
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { relayStylePagination } from '@apollo/client/utilities'
import { getSession } from 'next-auth/react'
import { ReactNode } from 'react'

import { Endpoints } from '@/constants/endpoints'

export const deviceTokenVar = makeVar('')
export const accountUrlVar = makeVar('')

const httpLink = createHttpLink({
  uri: Endpoints.CONNECT_URL
})

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      switch (err.extensions?.category) {
        case 'authentication':
        case 'authorization':
        // @TODO: Retry operation?
      }
    }
  }
})

const authLink = setContext(async (_, { headers }) => {
  const deviceToken = deviceTokenVar()
  let newHeaders = { ...headers }

  const session = await getSession()
  const accessToken = session?.accessToken

  if (accessToken) {
    newHeaders = {
      ...headers,
      authorization: `Bearer ${accessToken}`
    }
  }
  if (deviceToken) {
    newHeaders = {
      ...newHeaders,
      'X-DEVICE-TOKEN': deviceTokenVar()
    }
  }
  const modifiedHeader = {
    headers: newHeaders
  }
  return modifiedHeader
})

export const apolloClient = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Customer: {
        fields: {
          signatures: relayStylePagination(),
          signatureRequests: relayStylePagination(),
          accounts: relayStylePagination(),
          offers: relayStylePagination(),
          contracts: relayStylePagination(),
          scaleTickets: relayStylePagination(),
          outboundScaleTickets: relayStylePagination(),
          settlements: relayStylePagination()
        }
      },
      Company: {
        fields: {
          settings: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            }
          },
          locations: relayStylePagination(),
          cashBids: relayStylePagination()
        }
      },
      Query: {
        fields: {
          deviceToken: {
            read() {
              return deviceTokenVar()
            }
          },
          accountUrl: {
            read() {
              return accountUrlVar()
            }
          }
        }
      },
      MarketplaceSettings: {
        merge(existing, incoming, { mergeObjects }) {
          return mergeObjects(existing, incoming)
        }
      }
    }
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      returnPartialData: true
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all'
    },
    mutate: {
      errorPolicy: 'all'
    }
  }
})

export function AuthApolloProvider({ children }: { children: ReactNode }) {
  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}
